import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, Message, Table } from 'semantic-ui-react';

import styles from './SettingEmails.module.scss';

const SettingEmails = React.memo(
  ({
    canView,
    canEdit,
    settingEmails,
    isSubmitting,
    error,
    onCreate,
    onUpdate,
    onClearMessage,
  }) => {
    const [t] = useTranslation();
    const [settingEmail, setSettingEmail] = useState();
    const [fields, setFields] = useState([]);

    const handleAdd = () => {
      const values = [...fields];
      values.push({ email: '' });
      setFields(values);
    };

    const handleRemove = (i) => {
      const values = [...fields];
      values.splice(i, 1);
      setFields(values);
    };

    const handleChange = (i, event) => {
      const values = [...fields];
      values[i][event.target.name] = event.target.value;
      setFields(values);
    };

    const handleSubmit = useCallback(() => {
      const cleanData = {
        id: settingEmail?.id || '',
        emails: fields,
      };

      if (settingEmail.id) {
        onUpdate(cleanData);
      } else {
        onCreate(cleanData);
      }
    }, [fields, onCreate, onUpdate, settingEmail]);

    useEffect(() => {
      const setting = settingEmails?.length > 0 ? settingEmails[0] : {};
      setSettingEmail(setting);

      const fieldEmails = setting?.data?.emails || [];
      setFields(fieldEmails);
    }, [settingEmails]);

    return (
      <>
        {canView && (
          <div className={styles.wrapper}>
            <div className={styles.pageTitle}>
              <h2>{t('common.settingEmails.settingEmail')}</h2>
              <p>{t('common.settingEmails.settingEmailToReceiveAlert')}</p>
            </div>
            {error !== null && (
              <div className={styles.messageWrapper}>
                <Button
                  type="button"
                  icon="close"
                  size="tiny"
                  className={styles.closeMessage}
                  onClick={() => onClearMessage(false)}
                />
                {error ? (
                  <Message error header={t('common.settingEmails.saveFailed')} />
                ) : (
                  <Message success header={t('common.settingEmails.saveSuccessed')} />
                )}
              </div>
            )}
            <Form onSubmit={handleSubmit}>
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>#</Table.HeaderCell>
                    <Table.HeaderCell>{t('common.settingEmails.email')}</Table.HeaderCell>
                    <Table.HeaderCell>&nbsp;</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {fields.map((field, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Table.Row key={index}>
                      <Table.Cell>{index + 1}</Table.Cell>
                      <Table.Cell>
                        <Input
                          fluid
                          type="email"
                          name="email"
                          defaultValue={field.email}
                          readOnly={isSubmitting}
                          className={styles.field}
                          onChange={(e) => handleChange(index, e)}
                          disabled={!canEdit}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          className={styles.buttonRemoveEmail}
                          type="button"
                          onClick={() => handleRemove(index)}
                          disabled={!canEdit}
                        >
                          {t('common.settingEmails.removeEmail')}
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Footer fullWidth>
                  <Table.Row>
                    <Table.HeaderCell colSpan="5">
                      <Button
                        floated="right"
                        color="green"
                        icon="checkmark"
                        content={t('action.save')}
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      />
                      <Button
                        className={styles.buttonAddLevel}
                        type="button"
                        onClick={() => handleAdd()}
                        disabled={!canEdit}
                      >
                        {t('common.settingEmails.addEmail')}
                      </Button>
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Footer>
              </Table>
            </Form>
          </div>
        )}
      </>
    );
  },
);

SettingEmails.propTypes = {
  canView: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  settingEmails: PropTypes.arrayOf(PropTypes.any).isRequired,
  error: PropTypes.bool,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onClearMessage: PropTypes.func.isRequired,
};

SettingEmails.defaultProps = {
  error: null,
};

export default SettingEmails;
